import { alpha, createTheme, type PaletteMode } from '@mui/material/styles';

export const designTokens = {
  sidebarWidth: 220,
  collapsedSidebarWidth: 64,
  topBarHeight: 52,
  spacingUnit: 4,
  tableRowHeight: 40,
  borderRadius: 8,
  headerMaxWidth: 168,
  pagePadding: 20,
  cardPadding: 12,
  controlHeight: 38,
  iconButtonSize: 34,
  status: {
    critical: '#c62828',
    warning: '#a85b00',
    healthy: '#087451',
    overstock: '#2563a5',
    unknown: '#6b7280',
  },
} as const;

export const createAppTheme = (mode: PaletteMode) => {
  const dark = mode === 'dark';
  const surface = dark ? '#151b23' : '#ffffff';
  const subtleSurface = dark ? '#1b2430' : '#f6f8fa';
  const divider = dark ? '#2c3745' : '#dce3e9';
  const textPrimary = dark ? '#edf3f8' : '#17212b';
  const textSecondary = dark ? '#aebdca' : '#55616d';

  return createTheme({
    spacing: designTokens.spacingUnit,
    shape: { borderRadius: designTokens.borderRadius },
    palette: {
      mode,
      primary: {
        main: dark ? '#5fd0a0' : '#087451',
        dark: dark ? '#2ca875' : '#05583e',
        light: dark ? '#174c3b' : '#d9f1e7',
        contrastText: dark ? '#071d15' : '#ffffff',
      },
      secondary: { main: dark ? '#aebdca' : '#52606d' },
      background: { default: dark ? '#0d1419' : '#f3f6f5', paper: surface },
      error: { main: dark ? '#ff8c8c' : designTokens.status.critical },
      warning: { main: dark ? '#ffbc6f' : designTokens.status.warning },
      success: { main: dark ? '#70d7a0' : designTokens.status.healthy },
      info: { main: dark ? '#72b9f5' : designTokens.status.overstock },
      text: { primary: textPrimary, secondary: textSecondary },
      divider,
    },
    typography: {
      fontFamily:
        'Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      fontSize: 14,
      body1: { fontSize: '0.875rem', lineHeight: 1.45 },
      body2: { fontSize: '0.8125rem', lineHeight: 1.4 },
      caption: { fontSize: '0.6875rem', lineHeight: 1.35 },
      h1: { fontSize: '1.45rem', fontWeight: 760, lineHeight: 1.2, letterSpacing: '-0.018em' },
      h2: { fontSize: '1.05rem', fontWeight: 720, lineHeight: 1.3 },
      h3: { fontSize: '0.9rem', fontWeight: 720, lineHeight: 1.35 },
      subtitle2: { fontSize: '0.69rem', fontWeight: 760, letterSpacing: '0.075em' },
      button: { textTransform: 'none', fontWeight: 650 },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          html: { width: '100%', maxWidth: '100%', overflowX: 'hidden' },
          body: {
            width: '100%',
            maxWidth: '100%',
            overflowX: 'hidden',
            backgroundColor: dark ? '#0d1419' : '#f3f6f5',
          },
          '#root': { width: '100%', maxWidth: '100%', minHeight: '100dvh', overflowX: 'hidden' },
          '*': { scrollbarColor: `${dark ? '#536273' : '#aeb9c4'} transparent` },
          '*:focus-visible': {
            outline: `2px solid ${dark ? '#75deb3' : '#087451'}`,
            outlineOffset: 2,
          },
        },
      },
      MuiButton: {
        defaultProps: { size: 'small', disableElevation: true },
        styleOverrides: { root: { minHeight: designTokens.controlHeight, borderRadius: 7 } },
      },
      MuiIconButton: {
        defaultProps: { size: 'small' },
        styleOverrides: {
          root: { width: designTokens.iconButtonSize, height: designTokens.iconButtonSize },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: { root: { minHeight: designTokens.controlHeight } },
      },
      MuiTextField: { defaultProps: { size: 'small', variant: 'outlined' } },
      MuiFormControl: { defaultProps: { size: 'small', variant: 'outlined' } },
      MuiPaper: {
        defaultProps: { variant: 'outlined' },
        styleOverrides: {
          root: { backgroundImage: 'none', borderColor: divider, backgroundColor: surface },
        },
      },
      MuiCard: {
        styleOverrides: { root: { border: `1px solid ${divider}`, boxShadow: 'none' } },
      },
      MuiCardContent: {
        styleOverrides: { root: { padding: 12, '&:last-child': { paddingBottom: 12 } } },
      },
      MuiTableContainer: { styleOverrides: { root: { borderRadius: designTokens.borderRadius } } },
      MuiTableCell: {
        styleOverrides: {
          root: { padding: '6px 8px', borderColor: divider, verticalAlign: 'middle' },
          head: {
            color: textSecondary,
            fontSize: '0.69rem',
            fontWeight: 760,
            letterSpacing: '0.045em',
            lineHeight: 1.15,
            maxWidth: designTokens.headerMaxWidth,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            overflowWrap: 'normal',
            wordBreak: 'normal',
            backgroundColor: subtleSurface,
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            height: designTokens.tableRowHeight,
            '&:hover > .MuiTableCell-root': {
              backgroundColor: alpha(dark ? '#78d7ae' : '#087451', 0.065),
            },
          },
        },
      },
      MuiChip: {
        defaultProps: { size: 'small' },
        styleOverrides: { root: { height: 24, fontSize: '0.72rem', fontWeight: 700 } },
      },
      MuiDialogTitle: { styleOverrides: { root: { padding: '14px 16px 8px', fontWeight: 720 } } },
      MuiDialogContent: { styleOverrides: { root: { padding: '12px 16px' } } },
      MuiDialogActions: { styleOverrides: { root: { padding: '10px 16px 14px' } } },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            minHeight: 36,
            borderRadius: 7,
            marginBottom: 2,
            '&.Mui-selected': {
              backgroundColor: alpha(dark ? '#5fd0a0' : '#087451', dark ? 0.17 : 0.1),
              color: dark ? '#b9f3d7' : '#05583e',
              '&:hover': {
                backgroundColor: alpha(dark ? '#5fd0a0' : '#087451', dark ? 0.23 : 0.14),
              },
            },
          },
        },
      },
      MuiTooltip: {
        defaultProps: { arrow: true, enterDelay: 350 },
        styleOverrides: {
          tooltip: {
            backgroundColor: dark ? '#24312d' : '#16392d',
            color: '#ffffff',
            boxShadow: dark ? '0 8px 24px rgba(0,0,0,.55)' : '0 8px 24px rgba(5,40,28,.28)',
            fontSize: '0.75rem',
          },
          arrow: { color: dark ? '#24312d' : '#16392d' },
        },
      },
    },
  });
};

export const appTheme = createAppTheme('light');
